//Helper functions for dealing with manifests
import { ASSIGNMENT_SEQUENCE, ASSIGNMENT_STATUS, TEMPLATE_TO_ACTIVITYTYPE } from "./constants";

export function swapLocaleOrder(inManifest){
  if(!inManifest) return;
  let manifest = JSON.parse(JSON.stringify(inManifest));

  let enActivites = manifest.filter(act=>act.locale === "EN_US");
  let esActivities = manifest.filter(act=>act.locale === "ES_MX");

  //If we only have locale, then just return the current manifest
  if(!enActivites.length || !esActivities.length) return manifest;

  let insertBreak = enActivites[enActivites.length-1].sequence === ASSIGNMENT_SEQUENCE.BREAK || esActivities[esActivities.length-1].sequence === ASSIGNMENT_SEQUENCE.BREAK;
  let newManifest = [];
  const sequenceToStart = manifest[0].locale === "EN_US" ? esActivities : enActivites;
  const sequenceToEnd = manifest[0].locale === "EN_US" ? enActivites : esActivities;

  newManifest = sequenceToStart.map((act)=>{
    act.sequence = ASSIGNMENT_SEQUENCE.CONTINUE;
    return act;
  });

  if(insertBreak){
    sequenceToStart[sequenceToStart.length-1].sequence = ASSIGNMENT_SEQUENCE.BREAK;
    sequenceToEnd[sequenceToEnd.length-1].sequence = ASSIGNMENT_SEQUENCE.CONTINUE;
  }

  newManifest = newManifest.concat(sequenceToEnd);
  return newManifest;
}

export function removeBreak(inManifest){
  if(!inManifest) return;
  let manifest = JSON.parse(JSON.stringify(inManifest));

  return manifest.map((activity)=>{
    if(activity.sequence === ASSIGNMENT_SEQUENCE.BREAK){
      activity.sequence = ASSIGNMENT_SEQUENCE.CONTINUE
    }
    return activity;
  })
}

export function insertBreak(inManifest){
  if(!inManifest) return;
  let manifest = JSON.parse(JSON.stringify(inManifest));

  return manifest.map((activity,index)=>{
    if(index < manifest.length-1){
      if(manifest[index+1].locale !== activity.locale){
        activity.sequence = ASSIGNMENT_SEQUENCE.BREAK;
      }
    }
    return activity;
  });
}

export function getLocales(manifest){
  if(!manifest) return [];
  const localesInManifest = manifest.map((m) => m.locale);
  let dedupedLocales = [... new Set(localesInManifest)];
  return dedupedLocales
}

export function numBreaks(manifest){
  if(!manifest) return 0;
  let numLocales = getLocales(manifest).length;
  let breaks = manifest.reduce((breaks,act)=>{return act.sequence === ASSIGNMENT_SEQUENCE.BREAK && numLocales > 1 ? breaks+1 : breaks},0);
  if(breaks > 1) breaks = 1;
  return breaks;
}

export function swapLocales(inManifest){
  if(!inManifest) return;
  let manifest = JSON.parse(JSON.stringify(inManifest));
  return manifest.map((act)=>{
    act.locale = act.locale === "EN_US" ? "ES_MX" : "EN_US";
    return act
  });
}

export function filterLocales(inManifest, outputLocales){
  if(!inManifest || !outputLocales || !outputLocales.length) return inManifest;
  let manifest = JSON.parse(JSON.stringify(inManifest));

  return manifest.filter((act)=>outputLocales.includes(act.locale));
}

/* This method takes a manifest and transforms it based on the parameters:
    inManifest is the input manifest to transform
    outputLocales is an ordered list of the locales desired in the output
    twoSessions determines whether it is one or two sessions.

    A few notes:
    1. If the input manifest only contains one locale, it will be used to determine the activities in the other locale
    2. If the input activity contains both locales, they will be used as templates for each locale as the assignment is created
 */
export function transformManifest(inManifest,outputLocales,twoSessions){
  if(!inManifest || !outputLocales || !outputLocales.length) return;
  let manifest = JSON.parse(JSON.stringify(inManifest));
  const localesInManifest = getLocales(manifest);

  /* First deal with building the manifest
      Possible scenarios:
      1. The number of locales in the manifest is the same and order in which they appear in the input is the same - no op
      2. Both locales are in the manifest, but they're in the wrong order - swap them.
      3. The number of locales in the manifest is > the number in the output. Drop the one we don't want.
      4. The number of locales in the manifest is < the number in the output. Clone the one we want and put it in the right order
      5. One locale in manifest and one in output, but they're different locales - swap the locale on the input*/

  //This checks scenario #1 - if they're the same, then no further processing.
  if(JSON.stringify(localesInManifest) !== JSON.stringify(outputLocales)){
    if(localesInManifest.length === outputLocales.length){
      //Condition 2:  Both locales are in the manifest, but they're in the wrong order - swap them.
      if(localesInManifest.length === 2){
        manifest = swapLocaleOrder(manifest);
      }else{
       // Condition 5: One locale in manifest and one in output, but they're different locales - swap the locale on the input*/
        manifest = swapLocales(manifest);
      }
    }else{
      if(localesInManifest.length > outputLocales.length){
        //Condition 3 - The number of locales in the manifest is > the number in the output. Drop the one we don't want.
        manifest = filterLocales(manifest,outputLocales);
      }else{
        //Condition 4 - The number of locales in the manifest is < the number in the output. Clone the one we want and make a recursive call to put them in the right order.
        const newManifest = swapLocales(manifest);
        manifest = manifest.concat(newManifest);
        manifest = transformManifest(manifest,outputLocales,twoSessions);
      }
    }
  }

  //Honor twoSessions flag
  manifest = removeBreak(manifest);
  if(twoSessions){
    manifest = insertBreak(manifest);
  }
  return manifest;
}

export function getStatus(inAssignment){
  if(!inAssignment || !inAssignment.manifest) return;
  let manifest = JSON.parse(JSON.stringify(inAssignment)).manifest;

  let statuses = manifest.map((acttype)=>{
    return acttype.activity.reduce((status,act)=>{
      if(act.status === "underReview") return "underReview";
      if(act.displayStatus === "prereader") return "prereader";
      return status;
    },acttype.status);
  });

  if([ASSIGNMENT_STATUS.DONE,ASSIGNMENT_STATUS.COMPLETE].includes(inAssignment.status)){
    if(statuses.includes("underReview")) return "UNDER_REVIEW";
    if(statuses.includes("prereader")) return "PRE-READER";
  }

  return inAssignment.status;
}

export function cleanManifest(manifest){
  return manifest.map((item)=>{
    if(TEMPLATE_TO_ACTIVITYTYPE[item.activityType]){
      item.activityType = TEMPLATE_TO_ACTIVITYTYPE[item.activityType];
    }
    return item;
  })
}

export function insertLetterNamingAndSounds(manifest) {
  // Create the letterNamingAndSounds activity
  const createLetterNamingAndSoundsActivity = (locale) => ({
    activityType: "letterNamingAndSounds",
    locale: locale,
    sequence: "BREAK",
  });

  let lastEN_USIndex = -1;
  let lastES_MXIndex = -1;

  // Find the last index of the locale in the manifest
  for (let i = 0; i < manifest.length; i++) {
    if (manifest[i].locale === "EN_US") {
      lastEN_USIndex = i;
    } else if (manifest[i].locale === "ES_MX") {
      lastES_MXIndex = i;
    }
  }

  let newManifest = [...manifest];

  // Insert the letterNamingAndSounds activity after the last locale
  if (lastEN_USIndex !== -1) {
    newManifest = [
      ...newManifest.slice(0, lastEN_USIndex + 1),
      createLetterNamingAndSoundsActivity("EN_US"),
      ...newManifest.slice(lastEN_USIndex + 1),
    ];
    newManifest[lastEN_USIndex].sequence = "BREAK";
  }

  if (lastES_MXIndex !== -1) {
    const adjustedIndex = lastES_MXIndex + (lastEN_USIndex !== -1 ? 1 : 0);
    newManifest = [
      ...newManifest.slice(0, adjustedIndex + 1),
      createLetterNamingAndSoundsActivity("ES_MX"),
      ...newManifest.slice(adjustedIndex + 1),
    ];
    newManifest[adjustedIndex].sequence = "BREAK";
  }

  return newManifest;
}


export function removeLetterNamingAndSounds(manifest){
  return manifest.filter((activity)=>activity.activityType !== "letterNamingAndSounds");
}

export function getAssignmentConfiguration(assignment) {
  const locales = getLocales(assignment.manifest);

  const twoSessions = numBreaks(assignment.manifest) > 0;

  switch (locales.length) {
    case 1:
      return locales[0] === "en-us" ? "SCREENER_CONFIG_ENGLISH_ONLY" : "SCREENER_CONFIG_SPANISH_ONLY";
    case 2:
      const englishFirst = locales[0] === "en-us";
      if (twoSessions) {
        return englishFirst ? "SCREENER_CONFIG_BOTH_TWO_SESSIONS_ENGLISH_FIRST" : "SCREENER_CONFIG_BOTH_TWO_SESSIONS";
      } else {
        return englishFirst ? "SCREENER_CONFIG_BOTH_ONE_SESSION_ENGLISH_FIRST" : "SCREENER_CONFIG_BOTH_ONE_SESSION";
      }
    default:
      throw new Error("Unexpected number of locales");
  }
}

export default {swapLocaleOrder,removeBreak,insertBreak,getLocales, numBreaks, swapLocales,filterLocales,transformManifest,getStatus,cleanManifest,getAssignmentConfiguration,insertLetterNamingAndSounds,removeLetterNamingAndSounds};
